import React, { useState } from 'react';
import { useQuery, useAction, getInfoSections, upsertInfoSection } from 'wasp/client/operations';
import { Edit2, X, Save, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { DEFAULT_OPEN_HOURS, parseOpenHoursDisplay } from './openHoursUtils';

export interface InfoSection {
  id: string;
  slug: string;
  title: string;
  icon: string | null;
  content: string;
  displayOrder: number;
  isVisible: boolean;
}

interface SectionEditorProps {
  section: InfoSection | null;
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
  nextOrder: number;
}

function SectionEditor({ section, locationId, onClose, onSaved, nextOrder }: SectionEditorProps) {
  const upsert = useAction(upsertInfoSection);
  const [form, setForm] = useState({
    slug: section?.slug ?? 'open-hours',
    title: section?.title ?? 'Office Hours',
    icon: section?.icon ?? 'clock',
    content: section?.content ?? DEFAULT_OPEN_HOURS,
    displayOrder: section?.displayOrder ?? nextOrder,
  });
  const [saving, setSaving] = useState(false);

  const handle = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, content: e.target.value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error('Content is required');
      return;
    }
    setSaving(true);
    try {
      await upsert({ locationId, ...form });
      toast.success(section ? 'Hours updated' : 'Hours added');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Edit Opening Hours</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea
              name="content"
              value={form.content}
              onChange={handle}
              rows={8}
              required
              placeholder={'Monday – Friday: 9:00 AM – 5:00 PM\nSat/Sun: Closed'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y font-mono"
            />
            <p className="text-xs text-gray-400 mt-1">Use one line for weekend hours, e.g. Sat/Sun: Closed</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Hours'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface OpeningHoursPanelProps {
  locationId: string;
  isAdmin: boolean;
  /** Compact card for hero header (replaces Details). */
  variant?: 'hero' | 'inline';
}

export function OpeningHoursPanel({ locationId, isAdmin, variant = 'hero' }: OpeningHoursPanelProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [toggling, setToggling] = useState(false);
  const upsert = useAction(upsertInfoSection);

  const { data: rawSections = [], isLoading, refetch } = useQuery(
    getInfoSections,
    { locationId },
    { enabled: !!locationId }
  );
  const sections = rawSections as InfoSection[];

  const openHoursSection = sections.find((s) => s.slug === 'open-hours') ?? null;
  const hoursVisible = openHoursSection ? openHoursSection.isVisible : true;
  const content = openHoursSection?.content ?? DEFAULT_OPEN_HOURS;
  const { rows } = parseOpenHoursDisplay(content);

  const handleEditHours = async () => {
    if (!openHoursSection && locationId) {
      try {
        await upsert({
          locationId,
          slug: 'open-hours',
          title: 'Office Hours',
          icon: 'clock',
          content: DEFAULT_OPEN_HOURS,
          displayOrder: 0,
          isVisible: true,
        });
        refetch();
      } catch {
        /* ignore */
      }
    }
    setShowEditor(true);
  };

  const handleToggleVisibility = async () => {
    if (!locationId) return;
    setToggling(true);
    try {
      await upsert({
        locationId,
        slug: 'open-hours',
        title: openHoursSection?.title ?? 'Office Hours',
        icon: openHoursSection?.icon ?? 'clock',
        content: openHoursSection?.content ?? DEFAULT_OPEN_HOURS,
        displayOrder: openHoursSection?.displayOrder ?? 0,
        isVisible: !hoursVisible,
      });
      await refetch();
      toast.success(hoursVisible ? 'Opening hours hidden' : 'Opening hours shown');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setToggling(false);
    }
  };

  if (!hoursVisible && !isAdmin) return null;

  if (isLoading && !!locationId) {
    return (
      <div
        className={`flex-shrink-0 bg-white/90 rounded-xl border border-indigo-100 shadow-sm flex items-center justify-center ${
          variant === 'hero' ? 'w-full p-6 sm:min-w-[200px] sm:w-auto' : 'py-10'
        }`}
      >
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!hoursVisible && isAdmin) {
    return (
      <div className="flex-shrink-0 bg-gray-50 rounded-xl border border-dashed border-gray-200 p-4 w-full sm:min-w-[200px] sm:w-auto flex flex-col items-center gap-2">
        <EyeOff className="w-5 h-5 text-gray-300" />
        <p className="text-xs text-gray-400 text-center">Opening hours hidden</p>
        <button
          onClick={handleToggleVisibility}
          disabled={toggling}
          className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1 disabled:opacity-50"
        >
          <Eye className="w-3 h-3" /> Show
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className={`flex flex-col flex-shrink-0 bg-white/90 backdrop-blur-sm rounded-xl border border-indigo-100 shadow-sm ${
          variant === 'hero' ? 'p-4 w-full sm:min-w-[200px] sm:max-w-[240px] sm:w-auto' : 'p-4 w-full'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-sm">Opening Hours</h3>
          {isAdmin && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleToggleVisibility}
                disabled={toggling}
                title="Hide opening hours"
                className="p-1 text-gray-400 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50 disabled:opacity-50"
              >
                <EyeOff className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleEditHours}
                title="Edit opening hours"
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-gray-500">{row.label}</span>
              <span className="text-sm font-semibold text-gray-800">{row.time}</span>
            </div>
          ))}
        </div>
      </div>

      {showEditor && locationId && (
        <SectionEditor
          section={openHoursSection}
          locationId={locationId}
          onClose={() => setShowEditor(false)}
          onSaved={() => refetch()}
          nextOrder={sections.length}
        />
      )}
    </>
  );
}
